import { Stack } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import AppBarChart from './BarChart';
import DateRangeFilters from '@shared/components/DateRangeFilters';
import { FilterTableButton } from '@shared/components/FilterTableButton';
import { usePersistentFilters } from '@shared/store';
import { useForm } from 'react-hook-form';
import { useVisitsQuery } from '../../visits/queries';
import { getButtonStyle } from '@shared/entity';
import { useState } from 'react';

type QuickRevenueFilter = 'today' | 'last7days' | 'last3months';

const QUICK_REVENUE_FILTERS: Record<
  QuickRevenueFilter,
  { label: string; from: () => Dayjs; to: () => Dayjs }
> = {
  today: {
    label: 'Dnes',
    from: () => dayjs().startOf('day'),
    to: () => dayjs().endOf('day'),
  },
  last7days: {
    label: '7 dní',
    from: () => dayjs().subtract(6, 'day').startOf('day'),
    to: () => dayjs().endOf('day'),
  },
  last3months: {
    label: '3 měsíce',
    from: () => dayjs().subtract(3, 'month').startOf('day'),
    to: () => dayjs().endOf('day'),
  },
};

const RevenueChart = () => {
  // const { isDemo } = useAuthContext();
  const [filters, updateFilter] = usePersistentFilters();
  const [activeFilter, setActiveFilter] = useState<QuickRevenueFilter | null>(null);

  const {
    revenue: {
      dates: { from, to },
    },
  } = filters;

  const dayjsFrom = dayjs(from);
  const dayjsTo = dayjs(to);

  const { control, setValue } = useForm<Partial<{ to: Dayjs; from: Dayjs }>>({
    defaultValues: {
      from: dayjsFrom,
      to: dayjsTo,
    },
  });

  const applyQuickFilter = (key: QuickRevenueFilter) => {
    const newFrom = QUICK_REVENUE_FILTERS[key].from();
    const newTo = QUICK_REVENUE_FILTERS[key].to();
    updateFilter(draft => {
      draft.revenue.dates.from = newFrom.toISOString();
      draft.revenue.dates.to = newTo.toISOString();
    });
    setValue('from', newFrom);
    setValue('to', newTo);
    setActiveFilter(key);
  };

  const { data: visitData } = useVisitsQuery({ query: { from: dayjsFrom, to: dayjsTo } });

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        {(Object.keys(QUICK_REVENUE_FILTERS) as QuickRevenueFilter[]).map(key => (
          <FilterTableButton
            key={key}
            text={QUICK_REVENUE_FILTERS[key].label}
            variant={getButtonStyle(activeFilter, key)}
            setTableView={() => applyQuickFilter(key)}
          />
        ))}
      </Stack>
      <DateRangeFilters
        control={control}
        onChangeTo={date => {
          setActiveFilter(null);
          updateFilter(draft => {
            draft.revenue.dates.to = date?.toISOString();
          });
        }}
        onChangeFrom={date => {
          setActiveFilter(null);
          updateFilter(draft => {
            draft.revenue.dates.from = date?.toISOString();
          });
        }}
      />
      <AppBarChart visitData={visitData ?? []} from={dayjsFrom} to={dayjsTo} />
    </Stack>
  );
};

export default RevenueChart;
